type LoadGoogleMapsOptions = {
  libraries?: string[];
};

let googleMapsPromise: Promise<typeof google> | null = null;

export function loadGoogleMaps(
  apiKey: string,
  options: LoadGoogleMapsOptions = {},
): Promise<typeof google> {
  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key not configured'));
  }

  if (window.google?.maps) {
    return Promise.resolve(window.google);
  }

  if (googleMapsPromise) {
    return googleMapsPromise;
  }

  googleMapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(
      'google-maps-javascript-api',
    ) as HTMLScriptElement | null;

    const handleLoad = () => {
      if (window.google?.maps) {
        resolve(window.google);
        return;
      }

      googleMapsPromise = null;
      reject(new Error('Google Maps failed to initialize'));
    };

    if (existingScript) {
      existingScript.addEventListener('load', handleLoad, { once: true });
      existingScript.addEventListener(
        'error',
        () => {
          googleMapsPromise = null;
          reject(new Error('Failed to load Google Maps script'));
        },
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    // The script is loaded once and its library set is fixed at first load —
    // libraries can't be added afterward. Whichever page loads Maps first would
    // otherwise decide the set for the whole session (the overview map loads
    // plain core, so a later places-autocomplete request silently gets a
    // places-less google.maps). Always fold in `places` so the set is a stable
    // superset regardless of load order.
    const libraries = Array.from(
      new Set(['places', ...(options.libraries ?? [])]),
    );

    script.id = 'google-maps-javascript-api';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries.join(',')}`;
    script.async = true;
    script.defer = true;
    script.onload = handleLoad;
    script.onerror = () => {
      googleMapsPromise = null;
      reject(new Error('Failed to load Google Maps script'));
    };

    document.head.appendChild(script);
  });

  return googleMapsPromise;
}
